'use client';

import * as React from 'react';
import { FecomImage } from '../images/fecom-image';
import { tailwindBreakpointSizesToImageSizes } from '~/utils/images/tailwind-breakpoints-to-image-sizes';
import { ProductMediaType, type components } from '~/fecom-api/schema';
import { EmblaCarousel } from '../carousel/embla-carousel';
import { cn } from '~/lib/utils';

export type ProductImageCarouselProps = {
  images?: components['schemas']['ProductImage'][];
  media?: components['schemas']['ProductMedia'][];
  fecomMediaBaseUrl: string;
  variant: 'pdp' | 'quick-view';
};

type CarouselItem = {
  index: number;
  url: string;
  name: string; // e.g. Alt Text/Video Title
  mediaType: 'image' | 'video';
};

export const ProductImageCarousel = ({
  images = [],
  fecomMediaBaseUrl,
  media = [],
  variant,
}: ProductImageCarouselProps) => {
  const carouselItems: CarouselItem[] = React.useMemo(() => {
    const items: CarouselItem[] = [];

    const videos = media?.filter((media) => {
      switch (media.mediaType) {
        case ProductMediaType.Vimeo:
        case ProductMediaType.YouTube:
          return true;

        default:
          return false;
      }
    });

    images?.forEach((image, index) => {
      if (videos.length > 0 && index === 0) {
        videos.forEach((video, videoIndex) => {
          items.push({
            index: videoIndex,
            url: video.urlEncodedDocumentName,
            name: `Video ${videoIndex}`,
            mediaType: 'video',
          });
        });
      }

      items.push({
        index,
        url: image.name,
        name: `Image ${index}`, // TODO: Add alt text if it's ever available from the API
        mediaType: 'image',
      });
    });

    return items;
  }, [images, media]);

  if (!carouselItems.length) {
    return null;
  }

  return (
    <div className="relative w-full">
      <EmblaCarousel
        getIndex={(data) => data.index}
        variant={variant == 'quick-view' ? 'compact' : 'full'}
        renderSlide={({ data, type, selected, index }) => {
          const thumbnailSizes =
            variant === 'pdp'
              ? tailwindBreakpointSizesToImageSizes(
                  {
                    sm: '82px',
                    md: '40px',
                    lg: '58px',
                    xl: '80px',
                    '2xl': '86px',
                  },
                  '80px', //base size
                )
              : tailwindBreakpointSizesToImageSizes(
                  // quick-view
                  {
                    sm: '82px',
                    md: '40px',
                    lg: '58px',
                    xl: '80px',
                    '2xl': '86px',
                  },
                  '80px', //base size
                );

          const mainSizes =
            variant === 'pdp'
              ? tailwindBreakpointSizesToImageSizes(
                  {
                    md: '50vw',
                  },
                  '100vw',
                )
              : tailwindBreakpointSizesToImageSizes(
                  {
                    md: '297px',
                  },
                  '100vw',
                );

          if (type === 'thumbnail') {
            return (
              <div
                className={cn(
                  'relative aspect-square w-full overflow-hidden rounded-lg bg-white',
                )}
              >
                <FecomImage
                  src={data.url}
                  alt={data.name}
                  fill
                  priority={index === 0}
                  sizes={thumbnailSizes}
                  className="absolute inset-0 object-contain"
                  fecomMediaBaseUrl={fecomMediaBaseUrl}
                  loading={data.index < 5 ? 'eager' : 'lazy'}
                />
              </div>
            );
          }

          return (
            <FecomImage
              src={data.url}
              alt={data.name}
              fill
              loading={selected ? 'eager' : 'lazy'}
              sizes={mainSizes}
              className="absolute inset-0 object-contain"
              fecomMediaBaseUrl={fecomMediaBaseUrl}
            />
          );
        }}
        slides={carouselItems}
      />
    </div>
  );
};
